import { Entity } from '@api/api/entity-schema';
import { ColumnMeta, FilterResultMethod } from '@api/api/screener';
import { mapEntity } from '@api/mappers/mapEntity';
import { type AppTableFeatures } from '@design/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { OneDayReturnCell } from '@terminal/components/one-day-return-cell/OneDayReturnCell';
import { LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD } from '@terminal/views/screener/results/constants';
import { ScreenerCell } from '@terminal/views/screener/screener-cell/ScreenerCell';
import { CompetingThemesCell } from '@terminal/widgets/knowledge-graph-widget/components/competitors-tab/competing-themes-cell/CompetingThemesCell';
import { CompetitorsTableData } from '@terminal/widgets/knowledge-graph-widget/hooks/useCompetitorsData';
import { TFunction } from 'i18next';

import { TopRankIcon } from '../../top-rank-icon/TopRankIcon';
import { CompanyCell } from '../company-cell/CompanyCell';
import styles from './CompetitorsTable.module.scss';

const TABLE_FIELDS = {
  RANK: 'rank',
  COMPANY: 'tag',
  THEMES: 'themes',
  LIVE_ONE_DAY_RETURN: LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD,
  MARKET_CAP: 'market_cap.value',
};

export const getColumns = ({
  t,
  columnsMeta,
}: {
  t: TFunction;
  columnsMeta: ColumnMeta[];
}) => {
  const columns: ColumnDef<AppTableFeatures, CompetitorsTableData>[] = [
    {
      id: TABLE_FIELDS.RANK,
      header: t('widget:assetOverview.knowledgeGraph.competitors.columns.rank'),
      cell: props => {
        const { rank } = props.row.original;

        return (
          <div className={styles.rankWrapper}>
            <TopRankIcon rank={rank} />
          </div>
        );
      },
    },
    {
      id: TABLE_FIELDS.COMPANY,
      header: t(
        'widget:assetOverview.knowledgeGraph.competitors.columns.company'
      ),
      cell: props => {
        const entity = props.row.original.entity;

        if (!entity) {
          return null;
        }

        return <CompanyCell entity={entity} />;
      },
    },
    {
      id: TABLE_FIELDS.THEMES,
      header: t(
        'widget:assetOverview.knowledgeGraph.competitors.columns.competingThemes'
      ),
      cell: props => {
        const themes = props.row.original.competingThemes.map(t => t.name);
        return <CompetingThemesCell themes={themes} />;
      },
    },
    {
      id: TABLE_FIELDS.LIVE_ONE_DAY_RETURN,
      header: columnsMeta.find(
        c => c.field === TABLE_FIELDS.LIVE_ONE_DAY_RETURN
      )?.name,
      accessorFn: row =>
        row[TABLE_FIELDS.LIVE_ONE_DAY_RETURN as keyof CompetitorsTableData],
      cell: props => {
        const entityData = props.row.original.entity as Entity | undefined;

        return (
          <OneDayReturnCell
            asset={entityData ? mapEntity(entityData) : undefined}
            showPercentageSuffix
          />
        );
      },
    },
    {
      id: TABLE_FIELDS.MARKET_CAP,
      header: columnsMeta.find(c => c.field === TABLE_FIELDS.MARKET_CAP)?.name,
      accessorFn: row =>
        row[TABLE_FIELDS.MARKET_CAP as keyof CompetitorsTableData],
      cell: ({ getValue }) => {
        const col = columnsMeta.find(c => c.field === TABLE_FIELDS.MARKET_CAP);
        const value = getValue() as FilterResultMethod;

        if (!col) {
          return null;
        }

        return <ScreenerCell value={value} columnMeta={col} />;
      },
    },
  ];

  return columns;
};
